/** Inspection score the sparkline's dashed line marks (from the demo). */
export const COMPLIANCE_TARGET = 80

const dateKey = value => String(value || '').slice(0, 10)

/**
 * Scheduled inspections first (soonest first), then everything else newest first.
 * @param {{Date: string, Status: string}[]} inspections
 */
export const orderInspections = inspections => {
    const list = inspections || []
    const scheduled = list.filter(i => i.Status === 'Scheduled').sort((a, b) => dateKey(a.Date).localeCompare(dateKey(b.Date)))
    const rest = list.filter(i => i.Status !== 'Scheduled').sort((a, b) => dateKey(b.Date).localeCompare(dateKey(a.Date)))
    return [...scheduled, ...rest]
}

/**
 * Completed inspections that have a numeric score, oldest first: the points of the trend line.
 * @param {{Date: string, Status: string, Score: number|null}[]} inspections
 * @returns {{ date: string, score: number }[]}
 */
export const trendPoints = inspections =>
    (inspections || [])
        .filter(i => i.Status === 'Completed' && typeof i.Score === 'number' && Number.isFinite(i.Score))
        .map(i => ({ date: i.Date, score: i.Score }))
        .sort((a, b) => dateKey(a.date).localeCompare(dateKey(b.date)))

/**
 * Geometry for an SVG sparkline on a 0-100 scale.
 * @param {{ date: string, score: number }[]} points oldest first
 * @param {{ width?: number, height?: number, pad?: number, target?: number }} [options]
 * @returns {{ path: string, dots: {x: number, y: number, score: number, date: string}[], targetY: number }}
 */
export const sparkline = (points, { width = 300, height = 80, pad = 8, target = COMPLIANCE_TARGET } = {}) => {
    const innerW = width - pad * 2
    const innerH = height - pad * 2
    const yFor = score => pad + innerH - (Math.max(0, Math.min(100, score)) / 100) * innerH
    const dots = points.map((p, index) => ({
        x: points.length === 1 ? width / 2 : pad + (innerW * index) / (points.length - 1),
        y: yFor(p.score),
        score: p.score,
        date: p.date,
    }))
    const path = dots.map((d, i) => `${i === 0 ? 'M' : 'L'}${d.x.toFixed(1)},${d.y.toFixed(1)}`).join(' ')
    return { path, dots, targetY: yFor(target) }
}

/** "Below target" styling hook for a score. */
export const isBelowTarget = score => typeof score === 'number' && score < COMPLIANCE_TARGET
